// src/pages/user/SupportPage.jsx
import { useCallback, useMemo, useRef, useState } from "react";
import {
  BookOpen, Boxes, LayoutGrid, Paintbrush2, LifeBuoy,
  CreditCard, ExternalLink, Upload, Paperclip, MailCheck, Loader2,
} from "lucide-react";
import { injectUserStyles } from "../userStyles";

const STYLE_ID = "support-page-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .sp-articles-grid {
      display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin-bottom:24px;
    }
    @media (max-width:1023px) { .sp-articles-grid { grid-template-columns:repeat(2,1fr); } }
    @media (max-width:599px)  { .sp-articles-grid { grid-template-columns:1fr; } }
    .sp-article-card {
      display:flex; flex-direction:column; gap:10px;
      padding:16px; border:1px solid #e8ecf0; border-radius:10px;
      background:#fff; text-decoration:none; transition:box-shadow 160ms, border-color 160ms;
    }
    .sp-article-card:hover { box-shadow:0 4px 14px rgba(0,0,0,.07); border-color:#d1d5db; }
    .sp-article-icon {
      width:38px; height:38px; border-radius:8px; background:#f4f5f7;
      display:flex; align-items:center; justify-content:center;
      color:#374151; flex-shrink:0; transition:background 160ms;
    }
    .sp-article-card:hover .sp-article-icon { background:rgba(0,0,0,.08); color:#111111; }
    .sp-article-title { font-size:13.5px; font-weight:600; color:#0c1118; }
    .sp-article-desc  { font-size:12px; color:#9ca3af; line-height:1.5; }
    .sp-article-link  {
      display:inline-flex; align-items:center; gap:5px;
      font-family:'DM Sans',sans-serif; font-size:10px; letter-spacing:.06em;
      color:#111111; margin-top:auto;
    }

    .sp-form-input, .sp-form-textarea {
      width:100%; padding:9px 12px; border:1px solid #e2e8f0; border-radius:7px;
      font-size:14px; color:#0c1118; font-family:'DM Sans',sans-serif;
      outline:none; transition:border-color 150ms, box-shadow 150ms;
      box-sizing:border-box;
    }
    .sp-form-input:focus, .sp-form-textarea:focus {
      border-color:#111111; box-shadow:0 0 0 3px rgba(0,0,0,.08);
    }
    .sp-form-textarea { resize:vertical; min-height:120px; }
    .sp-file-zone {
      border:2px dashed #e2e8f0; border-radius:8px; padding:24px;
      text-align:center; cursor:pointer; transition:border-color 160ms, background 160ms;
    }
    .sp-file-zone:hover, .sp-file-zone.drag { border-color:#111111; background:rgba(0,0,0,.02); }
    .sp-success-banner {
      padding:32px 24px; text-align:center;
    }
    .sp-success-icon {
      width:52px; height:52px; border-radius:50%;
      background:rgba(16,185,129,.1); display:flex; align-items:center; justify-content:center;
      margin:0 auto 14px;
    }
  `;
  document.head.appendChild(el);
}

async function fetchJson(url, opts) {
  const r = await fetch(url, { credentials: "include", ...opts });
  if (!r.ok) throw new Error(await r.text().catch(() => r.statusText));
  return r.json();
}

const DOCS_BASE = process.env.REACT_APP_LANDING_URL || "https://mydesignerplus.com";

export default function SupportHelpPage() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [file,    setFile]    = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [sent,    setSent]    = useState(false);
  const [drag,    setDrag]    = useState(false);
  const inputRef = useRef(null);

  useState(() => { injectLocal(); });

  const canSubmit = subject.trim().length >= 3 && message.trim().length >= 10 && !busy;

  const articles = useMemo(() => [
    { key: "getting-started", title: "Getting Started",       desc: "Create your first project and navigate the interface.",              icon: BookOpen,    href: `${DOCS_BASE}/docs#docs-quickstart`      },
    { key: "designer-3d",     title: "Using the 3D Editor",   desc: "Dimensions, walls, openings, lean-tos, colors, and more.",           icon: Boxes,       href: `${DOCS_BASE}/docs#docs-editor-overview` },
    { key: "takeoff",         title: "Takeoff & Estimating",  desc: "Understand your material list and export it to PDF or CSV.",         icon: LayoutGrid,  href: `${DOCS_BASE}/docs#docs-takeoff`         },
    { key: "quotes",          title: "Requesting Quotes",     desc: "Submit your design for a formal quote and track its status.",        icon: ExternalLink, href: `${DOCS_BASE}/docs#docs-quotes`          },
    { key: "branding",        title: "Custom Branding",       desc: "Set up your company branding and white-label settings.",            icon: Paintbrush2, href: `${DOCS_BASE}/docs#docs-branding`        },
    { key: "billing",         title: "Billing & Plans",       desc: "Manage your subscription, invoices, and plan upgrades.",            icon: CreditCard,  href: `${DOCS_BASE}/docs#docs-subscription`    },
  ], []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("subject", subject.trim());
      fd.append("message", message.trim());
      if (file) fd.append("attachment", file);
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/ticket`, {
        method: "POST", credentials: "include", body: fd,
      });
      setSent(true);
    } catch {
      /* silent — user sees no change, can retry */
    } finally {
      setBusy(false);
    }
  }, [canSubmit, subject, message, file]);

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", maxWidth: 960, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <div className="up-title">Support &amp; Help</div>
        <div className="up-sub">Browse articles or contact our team</div>
      </div>

      {/* Articles */}
      <div className="sp-articles-grid">
        {articles.map((a) => (
          <a key={a.key} href={a.href} target="_blank" rel="noreferrer" className="sp-article-card">
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <div className="sp-article-icon"><a.icon size={18} /></div>
              <div>
                <div className="sp-article-title">{a.title}</div>
                <div className="sp-article-desc">{a.desc}</div>
              </div>
            </div>
            <div className="sp-article-link">
              Read article <ExternalLink size={11} />
            </div>
          </a>
        ))}
      </div>

      {/* Contact form */}
      <div className="up-panel">
        <div className="up-panel-head">
          <div className="up-panel-title">
            <LifeBuoy size={16} style={{ color: "#0c1118" }} />
            Contact Support
          </div>
        </div>

        {sent ? (
          <div className="sp-success-banner">
            <div className="sp-success-icon">
              <MailCheck size={22} style={{ color: "#10b981" }} />
            </div>
            <div style={{ fontSize: "16px", fontWeight: 600, color: "#0c1118", marginBottom: "6px" }}>Message Sent</div>
            <div style={{ fontSize: "13px", color: "#9ca3af", maxWidth: 360, margin: "0 auto 20px" }}>
              Our team will review your message and get back to you via email shortly.
            </div>
            <button className="up-btn up-btn-outline" onClick={() => { setSent(false); setSubject(""); setMessage(""); setFile(null); }}>
              Send Another Message
            </button>
          </div>
        ) : (
          <div style={{ padding: "20px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label className="up-field-label">Subject</label>
                <input
                  className="sp-form-input"
                  placeholder="Brief description of your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="up-field-label">Message</label>
                <textarea
                  className="sp-form-textarea"
                  placeholder="Describe your issue in detail…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              <div>
                <label className="up-field-label">Attachment (optional)</label>
                <div
                  className={`sp-file-zone ${drag ? "drag" : ""}`}
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
                  onClick={() => inputRef.current?.click()}
                >
                  <Paperclip size={20} style={{ color: "#9ca3af", margin: "0 auto 8px" }} />
                  {file ? (
                    <div style={{ fontSize: "13px", fontWeight: 500, color: "#0c1118" }}>{file.name}</div>
                  ) : (
                    <>
                      <div style={{ fontSize: "13px", fontWeight: 500, color: "#374151" }}>Click or drag to attach a file</div>
                      <div style={{ fontSize: "11.5px", color: "#9ca3af", marginTop: "3px" }}>PNG, JPG, PDF up to 10 MB</div>
                    </>
                  )}
                  <input ref={inputRef} type="file" style={{ display: "none" }} onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="up-btn up-btn-dark" onClick={handleSubmit} disabled={!canSubmit}>
                  {busy ? <><Loader2 size={14} style={{ animation: "upSpin .7s linear infinite" }} /> Sending…</> : <>Send Message</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}